import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  Check,
  GripVertical,
  Lock,
  Pencil,
  Search,
  Sparkles
} from 'lucide-react';
import toast from 'react-hot-toast';
import { getDocIntelligenceMappingRegistry } from '../../services/api/implementation/impl';
import { btnSecondary, DocIntelSkeleton } from './DocIntelUi';
import {
  classifyFieldStatus,
  FIELD_STATUS,
  FIELD_STATUS_ORDER,
  getFieldDescription,
  getFieldLabel,
  groupPlaceholdersByStatus
} from './docIntelUtils';
import { describeApplicationMapping, suggestMappingForPlaceholder } from './mappingRules';

function buildRowsFromMappings(fieldMappings = [], placeholders = []) {
  if (fieldMappings.length > 0) {
    const rows = fieldMappings.map((row) => ({
      placeholder_name: row.placeholder_name,
      target_type: row.target_type || '',
      target_path: row.target_path ?? '',
      static_value: row.static_value ?? '',
      source: 'manual'
    }));
    const existing = new Set(rows.map((r) => r.placeholder_name));
    for (const name of placeholders) {
      if (existing.has(name)) continue;
      const rule = suggestMappingForPlaceholder(name);
      rows.push({
        placeholder_name: name,
        target_type: rule?.target_type || '',
        target_path: rule?.target_path || '',
        static_value: '',
        source: rule ? 'rules' : 'unmapped'
      });
    }
    return rows.map(normalizeComputedRow);
  }

  return placeholders.map((name) => {
    const rule = suggestMappingForPlaceholder(name);
    if (!rule) {
      return {
        placeholder_name: name,
        target_type: '',
        target_path: '',
        static_value: '',
        source: 'unmapped'
      };
    }
    return normalizeComputedRow({
      placeholder_name: name,
      target_type: rule.target_type,
      target_path: rule.target_path || '',
      static_value: '',
      source: 'rules'
    });
  });
}

function normalizeComputedRow(row) {
  const rule = suggestMappingForPlaceholder(row.placeholder_name);
  if (rule?.target_type === 'computed') {
    return {
      ...row,
      target_type: 'computed',
      target_path: rule.target_path || row.target_path || row.placeholder_name,
      static_value: ''
    };
  }
  if (
    row.target_type === 'static' &&
    (row.target_path === 'computed' || /^sale\./.test(row.placeholder_name || ''))
  ) {
    const path = rule?.target_path || row.placeholder_name;
    return {
      ...row,
      target_type: 'computed',
      target_path: path,
      static_value: ''
    };
  }
  return row;
}

/** Remap a row into another Studio bucket (From deal / You fill in / Needs attention). */
function moveRowToBucket(row, targetStatus) {
  const name = row.placeholder_name;
  if (targetStatus === 'table') {
    if ((name || '').startsWith('table.')) {
      return {
        ok: true,
        row: {
          ...row,
          target_type: 'computed',
          target_path: name,
          static_value: ''
        }
      };
    }
    return {
      ok: false,
      reason: 'Only pricing / schedule table fields can sit in Auto table'
    };
  }
  if (targetStatus === 'user_fill') {
    return {
      ok: true,
      row: {
        ...row,
        target_type: 'static',
        target_path: '',
        static_value: row.static_value || '',
        source: 'manual'
      },
      openEditor: !String(row.static_value || '').trim()
    };
  }
  if (targetStatus === 'needs_attention') {
    return {
      ok: true,
      row: {
        ...row,
        target_type: '',
        target_path: '',
        static_value: '',
        source: 'unmapped'
      }
    };
  }
  if (targetStatus === 'from_deal') {
    const rule = suggestMappingForPlaceholder(name);
    if (rule?.target_type) {
      return {
        ok: true,
        row: normalizeComputedRow({
          placeholder_name: name,
          target_type: rule.target_type,
          target_path: rule.target_path || '',
          static_value: '',
          source: 'rules'
        })
      };
    }
    return {
      ok: true,
      row: {
        ...row,
        target_type: '',
        target_path: '',
        static_value: '',
        source: 'unmapped'
      },
      openEditor: true,
      toast: 'Pick a CRM field to finish “From deal”'
    };
  }
  return { ok: false, reason: 'Unknown bucket' };
}

function toParentMappings(rows) {
  return rows
    .filter((row) => {
      if (!row.target_type) return false;
      // Keep empty static so “You fill in” bucket moves stick before Save value
      if (row.target_type === 'static') return true;
      if (row.target_type === 'computed') return true;
      return Boolean(row.target_path);
    })
    .map((row) => {
      const mapping = {
        placeholder_name: row.placeholder_name,
        target_type: row.target_type,
        target_path: row.target_path || undefined
      };
      if (row.target_type === 'static') {
        mapping.static_value = row.static_value ?? '';
      }
      return mapping;
    });
}

function describeConnection(row, fieldLabels) {
  if (row.target_type === 'computed') {
    return 'Unit price + GST — calculated at PDF time';
  }
  if (row.target_type === 'static') {
    return row.static_value
      ? `Fixed: “${row.static_value}”`
      : 'You fill in — type a value, then Save value';
  }
  if (row.target_type === 'application_column') {
    return (
      describeApplicationMapping(row.placeholder_name, row.target_path) ||
      getFieldDescription(row.placeholder_name, fieldLabels) ||
      'From the Application form'
    );
  }
  if (row.target_type === 'dynamic_field' && row.target_path) {
    return `From deal · ${getFieldLabel(row.target_path, fieldLabels)}`;
  }
  if (!row.target_type) return 'Not connected yet';
  return getFieldDescription(row.placeholder_name, fieldLabels) || 'Connected';
}

function StatusInline({ status, isComputed }) {
  if (isComputed) {
    return (
      <span
        className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600"
        title="Calculated from unit price + GST — cannot type a fixed value">
        <Lock className="h-3.5 w-3.5" />
        Calculated
      </span>
    );
  }
  if (status === 'needs_attention') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-800">
        <AlertTriangle className="h-3.5 w-3.5" />
        Review
      </span>
    );
  }
  if (status === 'from_deal') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-800">
        <Check className="h-3.5 w-3.5" />
        From deal
      </span>
    );
  }
  if (status === 'user_fill') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-semibold text-sky-800">
        <Pencil className="h-3.5 w-3.5" />
        You fill in
      </span>
    );
  }
  if (status === 'table') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-semibold text-teal-800">
        Auto table
      </span>
    );
  }
  return null;
}

/**
 * Mockup-style field panel: search, flagged filter, section groups, row cards.
 */
const DocIntelFieldPanel = ({
  placeholders = [],
  fieldMappings = [],
  sampleLeaks = [],
  onChange,
  onInsertField,
  disabled = false,
  activeField = null,
  onHoverField,
  onLocateInDocument
}) => {
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all'); // all | review
  const [openEditor, setOpenEditor] = useState(null);
  const rowRefs = useRef({});

  const registryQuery = useQuery({
    queryKey: ['docIntelligenceRegistry'],
    queryFn: getDocIntelligenceMappingRegistry,
    staleTime: 5 * 60 * 1000
  });
  const fieldLabels = registryQuery.data?.field_labels;
  const appColumns = registryQuery.data?.application_columns ?? [];
  const dynamicFieldKeys = registryQuery.data?.dynamic_field_keys ?? [];

  useEffect(() => {
    setRows(buildRowsFromMappings(fieldMappings, placeholders));
  }, [fieldMappings, placeholders]);

  const emitChange = useCallback(
    (nextRows) => {
      setRows(nextRows);
      onChange?.(toParentMappings(nextRows));
    },
    [onChange]
  );

  const updateByName = (name, patch) => {
    const next = rows.map((row) =>
      row.placeholder_name === name ? normalizeComputedRow({ ...row, ...patch }) : row
    );
    emitChange(next);
  };

  const rowByName = useMemo(() => {
    const map = new Map();
    for (const row of rows) map.set(row.placeholder_name, row);
    return map;
  }, [rows]);

  // Prefer local rows so Move to updates buckets immediately (before parent props catch up).
  const effectiveMappings = useMemo(() => {
    const byName = new Map();
    for (const m of fieldMappings || []) {
      const key = (m.placeholder_name || '').toLowerCase();
      if (key) byName.set(key, m);
    }
    for (const row of rows) {
      const key = (row.placeholder_name || '').toLowerCase();
      if (!key) continue;
      if (!row.target_type) {
        byName.delete(key);
        continue;
      }
      byName.set(key, {
        placeholder_name: row.placeholder_name,
        target_type: row.target_type,
        target_path: row.target_path,
        static_value: row.static_value ?? ''
      });
    }
    return [...byName.values()];
  }, [fieldMappings, rows]);

  const needsReviewNames = useMemo(
    () =>
      placeholders.filter((name) => {
        const status = classifyFieldStatus(name, effectiveMappings, sampleLeaks);
        return status === 'needs_attention';
      }),
    [placeholders, effectiveMappings, sampleLeaks]
  );

  const connectedCount = useMemo(
    () =>
      placeholders.filter((name) => {
        const status = classifyFieldStatus(name, effectiveMappings, sampleLeaks);
        return status !== 'needs_attention';
      }).length,
    [placeholders, effectiveMappings, sampleLeaks]
  );

  const filteredNames = useMemo(() => {
    const q = query.trim().toLowerCase();
    return placeholders.filter((name) => {
      if (filter === 'review' && !needsReviewNames.includes(name)) return false;
      if (!q) return true;
      const label = getFieldLabel(name, fieldLabels).toLowerCase();
      const desc = (getFieldDescription(name, fieldLabels) || '').toLowerCase();
      return label.includes(q) || name.toLowerCase().includes(q) || desc.includes(q);
    });
  }, [placeholders, filter, needsReviewNames, query, fieldLabels]);

  const sections = useMemo(
    () => groupPlaceholdersByStatus(filteredNames, effectiveMappings, sampleLeaks),
    [filteredNames, effectiveMappings, sampleLeaks]
  );

  const moveField = (name, targetStatus) => {
    const row = rowByName.get(name) || {
      placeholder_name: name,
      target_type: '',
      target_path: '',
      static_value: ''
    };
    const currentStatus = classifyFieldStatus(name, effectiveMappings, sampleLeaks);
    if (currentStatus === targetStatus && targetStatus !== 'from_deal') {
      // Already there — still open editor for empty You fill in
      if (targetStatus === 'user_fill' && !String(row.static_value || '').trim()) {
        setOpenEditor(name);
      }
      return;
    }
    const result = moveRowToBucket(row, targetStatus);
    if (!result.ok) {
      toast.error(result.reason || 'Cannot move there');
      return;
    }
    const next = rows.some((r) => r.placeholder_name === name)
      ? rows.map((r) => (r.placeholder_name === name ? result.row : r))
      : [...rows, result.row];
    emitChange(next);
    // Leave "need review" filter so the field is visible in its new bucket
    if (filter === 'review' && targetStatus !== 'needs_attention') {
      setFilter('all');
    }
    if (targetStatus === 'user_fill') {
      setOpenEditor(name);
      toast.success('Moved to You fill in — type the value here, then Save value');
      return;
    }
    if (result.openEditor) setOpenEditor(name);
    else setOpenEditor(null);
    if (result.toast) toast(result.toast);
    else {
      const label = FIELD_STATUS[targetStatus]?.label || targetStatus;
      toast.success(`Moved to ${label} — Save draft to keep`);
    }
  };

  useEffect(() => {
    if (!activeField) return;
    const el = rowRefs.current[activeField];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [activeField]);

  useEffect(() => {
    if (!openEditor) return;
    const el = rowRefs.current[openEditor];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [openEditor, sections]);

  if (registryQuery.isLoading && !rows.length) {
    return <DocIntelSkeleton rows={3} />;
  }

  if (!placeholders.length) {
    return (
      <p className="rounded-xl border border-dashed border-slate-200 py-8 text-center text-sm text-slate-500">
        No live fields yet. Approve a sample or rebuild from source.
      </p>
    );
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm sm:p-4">
      <div className="mb-2.5 flex items-center justify-between gap-2">
        <p className="text-base font-semibold text-slate-900">Fields</p>
        <span className="text-sm font-medium tabular-nums text-slate-600">{placeholders.length}</span>
      </div>

      <div className="relative mb-2.5">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search fields…"
          className="w-full rounded-lg border border-slate-200 bg-white py-2.5 pl-9 pr-3 text-sm font-medium text-slate-800 outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-600/15"
        />
      </div>

      <div className="mb-3 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => {
            if (!needsReviewNames.length) return;
            setFilter((f) => (f === 'review' ? 'all' : 'review'));
          }}
          className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold transition ${
            filter === 'review'
              ? 'bg-amber-600 text-white'
              : needsReviewNames.length
                ? 'bg-amber-50 text-amber-900 ring-1 ring-amber-200 hover:bg-amber-100'
                : 'cursor-default bg-slate-100 text-slate-400'
          }`}
          disabled={!needsReviewNames.length && filter !== 'review'}>
          <AlertTriangle className="h-4 w-4" />
          {needsReviewNames.length
            ? `${needsReviewNames.length} need review`
            : 'None need review'}
        </button>
        <button
          type="button"
          onClick={() => setFilter('all')}
          className={`rounded-lg px-3 py-1.5 text-sm font-semibold transition ${
            filter === 'all'
              ? 'bg-slate-800 text-white'
              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
          }`}>
          All · {connectedCount} ok
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => {
            emitChange(buildRowsFromMappings([], placeholders));
            toast.success('Connections refreshed from defaults');
          }}
          className="ml-auto inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 hover:text-teal-800 disabled:opacity-50"
          title="Reset mappings to defaults">
          <Sparkles className="h-3.5 w-3.5" />
          Defaults
        </button>
      </div>

      <p className="mb-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs font-medium text-slate-600">
        {FIELD_STATUS_ORDER.map((id) => {
          const s = FIELD_STATUS[id];
          return (
            <span key={s.id} className="inline-flex items-center gap-1.5">
              <span className={`h-2.5 w-2.5 rounded-full ${s.legendDot}`} />
              {s.label}
            </span>
          );
        })}
        <span className="font-normal text-slate-500">
          · Move to change bucket · then Save draft
        </span>
      </p>

      <div className="max-h-[min(520px,55vh)] space-y-3 overflow-y-auto pr-0.5">
        {sections.map(({ status: bucketId, label, placeholders: names, hint }) => (
          <div
            key={bucketId}
            onDragOver={(e) => {
              if (disabled) return;
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
            }}
            onDrop={(e) => {
              if (disabled) return;
              e.preventDefault();
              const name = e.dataTransfer.getData('application/x-studio-field');
              if (!name) return;
              moveField(name, bucketId);
            }}
            className="rounded-lg border border-dashed border-slate-200 bg-slate-50/40 p-2">
            <p className="mb-1.5 text-xs font-bold uppercase tracking-wide text-slate-700">
              {label} · {names.length}
            </p>
            <p className="mb-2 text-[11px] font-medium text-slate-500">{hint}</p>
            {!names.length ? (
              <p className="rounded-md border border-dashed border-slate-200 bg-white/70 px-2 py-3 text-center text-[11px] font-medium text-slate-400">
                Drop a field here or use Move to
              </p>
            ) : null}
            <ul className="space-y-2">
              {names.map((name) => {
                const row = rowByName.get(name) || {
                  placeholder_name: name,
                  target_type: '',
                  target_path: '',
                  static_value: ''
                };
                const status = classifyFieldStatus(name, effectiveMappings, sampleLeaks);
                const isComputed = row.target_type === 'computed';
                const isActive = activeField === name;
                const isOpen = openEditor === name;
                const needsReview = status === 'needs_attention' || !row.target_type;

                return (
                  <li
                    key={name}
                    ref={(el) => {
                      rowRefs.current[name] = el;
                    }}
                    onMouseEnter={() => onHoverField?.(name)}
                    onMouseLeave={() => onHoverField?.(null)}
                    className={`rounded-lg border px-3 py-3 transition ${
                      needsReview
                        ? 'border-amber-300 bg-amber-50/70'
                        : isActive
                          ? 'border-teal-400 bg-teal-50/50 ring-1 ring-teal-200'
                          : 'border-slate-200 bg-white'
                    }`}>
                    <div className="flex items-start gap-2">
                      <button
                        type="button"
                        draggable={!disabled}
                        title="Drag onto document to insert, or onto another bucket to move"
                        onDragStart={(e) => {
                          e.dataTransfer.setData('text/plain', `{{ ${name} }}`);
                          e.dataTransfer.setData('application/x-studio-field', name);
                          e.dataTransfer.effectAllowed = 'copyMove';
                        }}
                        onClick={() => onInsertField?.(name)}
                        disabled={disabled}
                        className="mt-0.5 shrink-0 rounded p-0.5 text-slate-500 hover:bg-white hover:text-slate-800 disabled:opacity-40">
                        <GripVertical className="h-4 w-4" />
                      </button>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <button
                            type="button"
                            className="min-w-0 text-left"
                            onClick={() => onLocateInDocument?.(name)}>
                            <p className="truncate text-sm font-semibold text-slate-900">
                              {getFieldLabel(name, fieldLabels)}
                            </p>
                          </button>
                          <StatusInline status={status} isComputed={isComputed} />
                        </div>
                        <p className="mt-1 text-xs font-medium leading-snug text-slate-700">
                          {getFieldDescription(name, fieldLabels) ||
                            (isComputed
                              ? 'Unit price + GST, cannot be typed'
                              : describeConnection(row, fieldLabels))}
                        </p>
                        {!isComputed && getFieldDescription(name, fieldLabels) ? (
                          <p className="mt-1 text-xs text-slate-600">
                            {describeConnection(row, fieldLabels)}
                          </p>
                        ) : null}

                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          <label className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-600">
                            Move to
                            <select
                              disabled={disabled || isComputed}
                              value={status}
                              onChange={(e) => moveField(name, e.target.value)}
                              className="rounded border border-slate-200 bg-white px-1.5 py-1 text-[11px] font-semibold text-slate-800 outline-none focus:border-teal-600 disabled:opacity-50"
                              title={
                                isComputed
                                  ? 'Calculated fields stay From deal / Auto table'
                                  : 'Move this field to another bucket'
                              }>
                              {FIELD_STATUS_ORDER.map((id) => (
                                <option
                                  key={id}
                                  value={id}
                                  disabled={
                                    id === 'table' && !(name || '').startsWith('table.')
                                  }>
                                  {FIELD_STATUS[id].label}
                                </option>
                              ))}
                            </select>
                          </label>
                          {isComputed ? null : status === 'needs_attention' ? (
                            <>
                              <button
                                type="button"
                                disabled={disabled}
                                onClick={() => moveField(name, 'user_fill')}
                                className="rounded-md bg-sky-700 px-2 py-1 text-[11px] font-bold text-white hover:bg-sky-800 disabled:opacity-50">
                                → You fill in
                              </button>
                              <button
                                type="button"
                                disabled={disabled}
                                onClick={() => {
                                  moveField(name, 'from_deal');
                                  setOpenEditor(name);
                                }}
                                className="text-xs font-bold text-teal-800 hover:underline disabled:opacity-50">
                                Connect to CRM
                              </button>
                            </>
                          ) : status === 'user_fill' && !isOpen ? (
                            <button
                              type="button"
                              disabled={disabled}
                              onClick={() => setOpenEditor(name)}
                              className="text-xs font-bold text-sky-800 hover:underline disabled:opacity-50">
                              {row.static_value
                                ? `Edit value (“${row.static_value}”)`
                                : 'Add value'}
                            </button>
                          ) : status === 'from_deal' && !isOpen ? (
                            <button
                              type="button"
                              disabled={disabled}
                              onClick={() => setOpenEditor(name)}
                              className="text-xs font-bold text-teal-800 hover:underline disabled:opacity-50">
                              Edit connection
                            </button>
                          ) : null}
                        </div>

                        {isComputed || !isOpen ? null : status === 'user_fill' ? (
                          <div className="mt-2 border-t border-sky-200/80 pt-2">
                            <StaticValueEditor
                              row={row}
                              disabled={disabled}
                              onPick={(patch) => updateByName(name, patch)}
                              onClose={() => setOpenEditor(null)}
                            />
                          </div>
                        ) : (
                          <div className="mt-2 border-t border-slate-200/80 pt-2">
                            <InlineConnectEditor
                              row={row}
                              disabled={disabled}
                              appColumns={appColumns}
                              dynamicFieldKeys={dynamicFieldKeys}
                              fieldLabels={fieldLabels}
                              defaultMode={status === 'needs_attention' ? 'crm' : undefined}
                              onPick={(patch) => {
                                updateByName(name, patch);
                                // Saving a fixed value from Needs attention still lands in You fill in
                                if (patch?.target_type === 'static') {
                                  setFilter('all');
                                }
                              }}
                              onClose={() => setOpenEditor(null)}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
        {!filteredNames.length && (
          <p className="py-6 text-center text-sm font-medium text-slate-600">
            No fields match this filter.
          </p>
        )}
      </div>
    </div>
  );
};

function StaticValueEditor({ row, disabled, onPick, onClose }) {
  const [fixedValue, setFixedValue] = useState(row.static_value || '');
  const valueRef = useRef(null);

  useEffect(() => {
    setFixedValue(row.static_value || '');
  }, [row.placeholder_name, row.static_value]);

  useEffect(() => {
    window.requestAnimationFrame(() => valueRef.current?.focus());
  }, [row.placeholder_name]);

  const saveFixed = () => {
    const value = String(fixedValue || '').trim();
    if (!value) {
      toast.error('Enter a value first (e.g. Park), then press Save value');
      valueRef.current?.focus();
      return;
    }
    onPick({
      target_type: 'static',
      target_path: '',
      static_value: value
    });
    toast.success(`Saved “${value}” — click Save draft to keep it`);
    onClose?.();
  };

  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold text-sky-900">
        You fill in — type the value for every deal on this project
      </p>
      <input
        ref={valueRef}
        disabled={disabled}
        type="text"
        value={fixedValue}
        onChange={(e) => setFixedValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            saveFixed();
          }
        }}
        className="w-full rounded-md border border-sky-200 bg-white px-2.5 py-2 text-sm font-semibold text-slate-900 outline-none focus:border-sky-600 focus:ring-2 focus:ring-sky-600/15"
        placeholder="e.g. Park, Madhya Pradesh, Nil…"
      />
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={disabled}
          onClick={saveFixed}
          className="inline-flex items-center justify-center rounded-lg bg-sky-700 px-3 py-1.5 text-xs font-bold text-white hover:bg-sky-800 disabled:opacity-50">
          Save value
        </button>
        <button
          type="button"
          className="text-xs font-semibold text-slate-600 hover:text-slate-900"
          onClick={onClose}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function InlineConnectEditor({
  row,
  disabled,
  appColumns,
  dynamicFieldKeys,
  fieldLabels,
  onPick,
  onClose,
  defaultMode
}) {
  const inferred =
    defaultMode ||
    (row.target_type === 'static' || !row.target_type || !row.target_path
      ? 'fixed'
      : 'crm');
  const [mode, setMode] = useState(inferred);
  const [fixedValue, setFixedValue] = useState(row.static_value || '');
  const [query, setQuery] = useState('');
  const valueRef = useRef(null);

  useEffect(() => {
    setFixedValue(row.static_value || '');
    setMode(
      defaultMode ||
        (row.target_type === 'static' || !row.target_type || !row.target_path
          ? 'fixed'
          : 'crm')
    );
  }, [
    row.placeholder_name,
    row.static_value,
    row.target_type,
    row.target_path,
    defaultMode
  ]);

  useEffect(() => {
    if (mode === 'fixed') {
      window.requestAnimationFrame(() => valueRef.current?.focus());
    }
  }, [mode]);

  const options = useMemo(() => {
    const list = [];
    for (const col of appColumns) {
      const path = col.startsWith('Application.') ? col : `Application.${col}`;
      const label = getFieldLabel(
        path.replace(/^Application\./i, 'applicant.'),
        fieldLabels
      );
      list.push({
        type: 'application_column',
        path,
        label: label || path,
        description: `Application · ${col}`
      });
    }
    for (const key of dynamicFieldKeys) {
      list.push({
        type: 'dynamic_field',
        path: key,
        label: getFieldLabel(key, fieldLabels),
        description: `Deal · ${key}`
      });
    }
    const q = query.trim().toLowerCase();
    if (!q) return list.slice(0, 30);
    return list
      .filter(
        (o) =>
          o.label.toLowerCase().includes(q) ||
          o.path.toLowerCase().includes(q) ||
          o.description.toLowerCase().includes(q)
      )
      .slice(0, 30);
  }, [appColumns, dynamicFieldKeys, fieldLabels, query]);

  const saveFixed = () => {
    const value = String(fixedValue || '').trim();
    if (!value) {
      toast.error('Enter a value first (e.g. Park), then press Save value');
      valueRef.current?.focus();
      return;
    }
    onPick({
      target_type: 'static',
      target_path: '',
      static_value: value
    });
    toast.success(`Saved “${value}” — click Save draft at the bottom to keep it`);
    onClose?.();
  };

  return (
    <div className="space-y-2.5">
      <div className="flex gap-1 rounded-lg bg-slate-100 p-0.5">
        <button
          type="button"
          disabled={disabled}
          onClick={() => setMode('fixed')}
          className={`flex-1 rounded-md px-2 py-1.5 text-xs font-bold transition ${
            mode === 'fixed' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600'
          }`}>
          Enter text only
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => setMode('crm')}
          className={`flex-1 rounded-md px-2 py-1.5 text-xs font-bold transition ${
            mode === 'crm' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600'
          }`}>
          Connect to CRM
        </button>
      </div>

      {mode === 'fixed' ? (
        <>
          <p className="text-xs font-medium text-slate-600">
            Prefer: Move to → You fill in, then type the value there.
          </p>
          <input
            ref={valueRef}
            disabled={disabled}
            type="text"
            value={fixedValue}
            onChange={(e) => setFixedValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                saveFixed();
              }
            }}
            className="w-full rounded-md border border-slate-200 px-2.5 py-2 text-sm font-semibold text-slate-900 outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-600/15"
            placeholder="e.g. Park"
          />
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={disabled}
              onClick={saveFixed}
              className="inline-flex items-center justify-center rounded-lg bg-teal-800 px-3 py-1.5 text-xs font-bold text-white hover:bg-teal-900 disabled:opacity-50">
              Save value
            </button>
            <button
              type="button"
              className="text-xs font-semibold text-slate-600 hover:text-slate-900"
              onClick={onClose}>
              Cancel
            </button>
          </div>
        </>
      ) : (
        <>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search CRM fields…"
            disabled={disabled}
            className="w-full rounded-md border border-slate-200 px-2.5 py-2 text-sm outline-none focus:border-teal-600"
          />
          <ul className="max-h-36 overflow-y-auto rounded-md border border-slate-200 bg-white">
            {options.map((opt) => (
              <li key={`${opt.type}:${opt.path}`}>
                <button
                  type="button"
                  disabled={disabled}
                  className="w-full px-2.5 py-2 text-left hover:bg-teal-50 disabled:opacity-50"
                  onClick={() => {
                    onPick({
                      target_type: opt.type,
                      target_path: opt.path,
                      static_value: ''
                    });
                    toast.success('Connected to CRM — click Save draft to keep it');
                    onClose?.();
                  }}>
                  <span className="block text-sm font-semibold text-slate-800">{opt.label}</span>
                  <span className="block text-xs text-slate-600">{opt.description}</span>
                </button>
              </li>
            ))}
            {!options.length && (
              <li className="px-2.5 py-2 text-sm text-slate-500">No CRM matches</li>
            )}
          </ul>
          <button
            type="button"
            className={`${btnSecondary} w-full !text-xs`}
            onClick={onClose}>
            Cancel
          </button>
        </>
      )}
    </div>
  );
}

export default DocIntelFieldPanel;
